/* eslint-disable jsx-a11y/anchor-is-valid */
import _ from 'underscore';
import $ from 'jquery';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { SearchBox, ActionButton, SelectionMode, Selection, Link } from '@fluentui/react';
import { CampaignShimmeredDetailsList } from '@bingads-webui-grid/campaign-details-list';
import { buildColumn } from '@bingads-webui-grid/campaignui-common-columns';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { getBmcMtEndpoint, hasError } from '@bingads-webui-campaign/cpg-product-feed-grid';
import { SelectedItem } from '@bingads-webui-campaign-react/pill-item-components';
import { ManuallyAddBrandModal } from '../brand-selector/manually-add-brand-modal';

const pageSize = 10;
const defaultColumn = ({ i18n }) => [
  {
    key: 'BrandName',
    title: i18n.getString(_TL_('Brand')),
    helpId: 'pop_BA_a',
  },
  {
    key: 'BrandUrl',
    title: i18n.getString(_TL_('Brand domain')),
    helpId: 'pop_BA_a',
  },
  {
    key: 'MatchedProductCount',
    title: i18n.getString(_TL_('Product count')),
    helpId: 'pop_BA_a',
  },
];

export const buildColumns = ({
  i18n,
}) => {
  const columns = defaultColumn({
    i18n,
  });
  return columns.map(column => ({
    ...buildColumn(column),
    onRender: column.onRender,
  }));
};

export const BrandSelector = (props) => {
  const {
    i18n,
    classes,
    onChange,
    scope,
    activity,
    logScope,
    perfMarker,
    brandsList,
  } = props;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [query, setQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const columns = useMemo(() => buildColumns({ i18n }), [i18n]);
  const gridSelectionRef = useRef(new Selection({
    onSelectionChanged: () => {
      const itemsInCurrentPage = gridSelectionRef.current.getItems();
      if (itemsInCurrentPage[0]) {
        const selection = gridSelectionRef.current.getSelection();
        setSelectedItems((v) => {
          const map = itemsInCurrentPage.reduce((acc, item) => {
            acc[item.Id] = true;
            return acc;
          }, {});
          const itemsInOtherPage = v.filter(item => !map[item.Id]);
          return [...itemsInOtherPage, ...selection];
        });
      }
    },
  }));

  const { aid, cid, uid } = scope.values;
  const AlertCenter = useMemo(() => new AlertHelper({ i18n }), [i18n]);

  const [showManuallyAddBrandDialog, setShowManuallyAddBrandDialog] = useState(false);

  const saveBrandToResultList = useCallback((brand) => {
    activity.trace(`${logScope}|AddBrandPanel|manually add brand: ${JSON.stringify(brand)}`);
    setSelectedItems([...selectedItems, brand]);
  }, [activity, logScope, selectedItems]);

  const setToEmptyResult = useCallback(() => {
    setItems([]);
    setTotal(0);
  }, []);

  const fetch = useCallback((params) => {
    const { query: _query, pageNumber: _pageNumber } = params;
    if (_.isEmpty(_query)) {
      setToEmptyResult();
      return;
    }
    const childPerfMarker = perfMarker.createChild('Simplified_SearchBrand');
    childPerfMarker.willInit();
    activity.trace(`${logScope}|BrandSelector|search brand request data: ${JSON.stringify(params)}`);
    setItems(null);
    $.ajax({
      url: `${getBmcMtEndpoint()}/api/v1/manufacturers/${cid}/brands/term-search-v2?cid=${cid}&aid=${aid}&uid=${uid}`,
      type: 'POST',
      xhrFields: {
        withCredentials: true,
      },
      data: `BrandNameTerm=${encodeURIComponent(_query)}&Page=${_pageNumber}&PageSize=${pageSize}`,
      success: (res) => {
        childPerfMarker.done();
        if (!hasError(res)) {
          AlertCenter.dismissAlert();
          const brandItems = _.map(_.result(res.ResponseObject, 'Brands', []), brand => ({ ...brand, Id: `${brand.BrandName}${brand.BrandUrl}` }));
          activity.trace(`${logScope}|BrandSelector|search brand request succeed: ${JSON.stringify(_.map(brandItems, brand => `${brand.BrandName}(${brand.BrandUrl})}`))}`);
          setItems(brandItems);
          setTotal(_.get(res.ResponseObject, 'Total', 0));
          brandItems.forEach((item, index) => {
            if (selectedItems.find(i => i.Id === item.Id)) {
              gridSelectionRef.current.setIndexSelected(index, true);
            }
          });
          return;
        }
        activity.error(`${logScope}|BrandSelector|search brand failed: ${res.Messages}`);
        AlertCenter.showAlert(res.Messages);
        setToEmptyResult();
      },
      error: (err) => {
        childPerfMarker.done();
        activity.error(`${logScope}|BrandSelector|search brand request failed: ${JSON.stringify(err)}`);
        AlertCenter.showAlert(_.get(err, ['responseJSON', 'Message']));
        setToEmptyResult();
      },
    });
  }, [perfMarker, activity, logScope, cid, aid, uid, setToEmptyResult, AlertCenter, selectedItems]);

  const removeAllSelection = useCallback(() => {
    setSelectedItems([]);
    for (let i = 0; i < (items && items.length) || 0; i += 1) {
      gridSelectionRef.current.setIndexSelected(i, false);
    }
  }, [items]);

  const removeItem = useCallback((item) => {
    setSelectedItems(v => v.filter(i => i.Id !== item.Id));
    const index = items.findIndex(i => i.Id === item.Id);
    gridSelectionRef.current.setIndexSelected(index, false);
  }, [items]);

  useEffect(() => {
    onChange(selectedItems);
  }, [onChange, selectedItems]);

  return (
    <div className={classes.BrandSelector}>
      <SearchBox
        placeholder="Search"
        onSearch={(value) => {
          setPageNumber(1);
          setQuery(value);
          fetch({ query: value, pageNumber: 1 });
        }}
      />
      <ActionButton iconProps={{ iconName: 'Add' }} onClick={() => setShowManuallyAddBrandDialog(true)} className="manually-add-btn">
        {i18n.getString(_TL_('Manually Add Brand'))}
      </ActionButton>
      <CampaignShimmeredDetailsList
        enableShimmer={items === null}
        items={items || []}
        columns={columns}
        selectionMode={SelectionMode.multiple}
        selection={gridSelectionRef.current}
        pager={{
          pageNumber,
          pageSize,
          total,
          availablePageSizes: [10],
          onPageChange: (page) => {
            setPageNumber(page);
            fetch({ query, pageNumber: page });
          },
        }}
      />
      <p className="selected-count">
        {i18n.getString(_TL_('{{count}} brands'), { count: selectedItems.length })}
        {selectedItems && selectedItems.length > 0 && <Link onClick={() => removeAllSelection()}>Remove all</Link>}
      </p>
      <div className="selected-items">
        {!_.isEmpty(selectedItems) && selectedItems.map((item, index) => (
          <SelectedItem
            key={item.Id}
            itemText={item.BrandName}
            itemIndex={index}
            onDelete={() => removeItem(item)}
          />
        ))}
      </div>
      <ManuallyAddBrandModal
        i18n={i18n}
        showDialog={showManuallyAddBrandDialog}
        setShowDialog={setShowManuallyAddBrandDialog}
        setBrand={saveBrandToResultList}
        brandsList={brandsList}
        selectedBrands={selectedItems}
      />
    </div>
  );
};

BrandSelector.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    BrandSelector: PropTypes.shape({}).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      aid: PropTypes.string.isRequired,
      cid: PropTypes.string.isRequired,
      uid: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  activity: PropTypes.shape({
    trace: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }).isRequired,
  logScope: PropTypes.string.isRequired,
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  brandsList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};
